import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, InputLabel, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import { memo, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import fileApi from '~/apis/file';
import orderApi from '~/apis/order';
import { CloseIcon, DeleteBin6LineIcon, UploadIcon } from '~/assets/icons';
import BaseRow from '~/components/common/base-row/BaseRow';
import { useAppDispatch } from '~/redux/hooks';
import { commonActions } from '~/thunks/common/commonSlice';
import { downloadFile, getFileName, handleApiError, notify } from '~/utils/helper';
import { IEquipment } from '~/utils/interface/equipment';
import { IEditOrder, IOrder, IPlan } from '~/utils/interface/order';

type OrderHistoryDialogProps = {
  open: boolean;
  setOpen?: (open: boolean) => void;
  data?: IOrder;
  plans: IPlan[];
  equipmentData?: IEquipment;
  agents: any[];
};
type StatusColor = { [key: string]: string };
const orderStatusColor: StatusColor = {
  0: '#F32A4C',
  1: '#F32A4C',
  2: '#3D9149',
  3: '#F32A4C',
  4: '#F32A4C',
};

const initialValues: IEditOrder = {
  files: [],
};

const OrderHistoryDialog = (props: OrderHistoryDialogProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation(['admin/equipment', 'admin/common']);
  const { data, plans, equipmentData, agents } = props;
  const handleSetOpen = (open: boolean) => {
    if (props.setOpen) {
      props.setOpen(open);
    }
  };
  const getOrderStatusDisplay = (status: number | undefined) => {
    if (status === undefined || status === null) {
      return null;
    }

    const color = orderStatusColor[String(status) as keyof StatusColor];

    return (
      <Box
        className={`text-[${color}] border border-[${color}] rounded-[15px] h-[25px] px-[10px] py-[1px] inline-block`}
        sx={{
          color: color,
          borderColor: color,
        }}
      >
        {t(`orderStatuses.${status}`)}
      </Box>
    );
  };
  const getAgentName = (agent: string | undefined) => {
    const agentInfo = agents.find((item) => item.username === agent);
    if (agentInfo) return `${agentInfo.username} - ${agentInfo.name}`;
    return agent || '';
  };

  const planDescription = data?.items?.[0]
    ? plans.find((plan) => plan.plan_id === data.items[0].plan_id)?.description
    : '';

  useEffect(() => {
    if (data) {
      form.setValues({
        files: data.einvoice_info.files || [],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const form = useFormik({
    validateOnChange: false,
    initialValues,
    onSubmit: async (values) => {
      try {
        await orderApi.updateEInvoiceFile({ order_id: data?.order_id, ...values });
        notify('success', t('saveSuccess', { ns: 'admin/common' }));
      } catch (e: any) {
        handleApiError(e, true);
      }
    },
  });

  const { getRootProps, getInputProps } = useDropzone({
    maxFiles: 1,
    onDrop: async (acceptedFiles: Array<File>) => {
      dispatch(commonActions.setIsLoading(true));
      try {
        const uploadForm = new FormData();
        uploadForm.append('file', acceptedFiles[0]);

        const response = await fileApi.uploadFiles(uploadForm);
        if (response.data) {
          form.setFieldValue('files', [...(form.values.files || []), response.data.url]);
        }
      } catch (e: any) {
        handleApiError(e);
      }

      dispatch(commonActions.setIsLoading(false));
    },
  });

  return (
    <Dialog
      open={props.open}
      onClose={() => handleSetOpen(false)}
      sx={{
        '& .MuiDialog-paper': {
          width: '488px',
          minWidth: '488px',
          minHeight: '508px',
          borderRadius: '20px',
        },
      }}
    >
      <DialogTitle className=' bg-[#005F73] '>
        <Typography variant='h6' className='font-bold text-white '>
          {t('orderHistoryDetail.detailTitle')}
        </Typography>
        <IconButton onClick={() => handleSetOpen(false)} className='text-white'>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          overflowY: 'auto',
          paddingLeft: '25px !important',
          paddingRight: '25px !important',
        }}
      >
        <Box className='flex flex-grow'>
          <Box className='w-[100%] pr-3 '>
            <Box>
              <Typography variant='body2' className='text-[13px]' style={{ lineHeight: 1.7, marginBottom: '4px' }}>
                <span className='font-bold inline-block'> {t('orderHistoryDetail.accountOrder')}:</span>
                <span className='pl-2 '>{data?.username || ''}</span>
              </Typography>
              <Typography variant='body2' className='text-[13px]' style={{ lineHeight: 1.7, marginBottom: '4px' }}>
                <span className='font-bold inline-block'>{t('orderHistoryDetail.orderTime')}:</span>
                <span className='pl-2'>
                  {data?.created_at ? dayjs(data.created_at).format('DD/MM/YYYY HH:mm') : ''}
                </span>
              </Typography>
              <Typography variant='body2' className='text-[13px]' style={{ lineHeight: 1.7, marginBottom: '4px' }}>
                <span className='font-bold inline-block'>{t('orderHistoryDetail.orderService')}:</span>
                <span className='pl-2 '>{planDescription}</span>
              </Typography>
              <Typography variant='body2' className='text-[13px]' style={{ lineHeight: 1.7, marginBottom: '4px' }}>
                <span className='font-bold inline-block'>{t('orderHistoryDetail.orderDevice')}:</span>
                <span className='pl-2 '>{equipmentData?.device_code || ''}</span>
              </Typography>
              <Typography variant='body2' className='text-[13px]' style={{ lineHeight: 1.7, marginBottom: '4px' }}>
                <span className='font-bold inline-block'>{t('orderHistoryDetail.orderSim')}:</span>
                <span className='pl-2 '>{equipmentData?.sim_no || ''}</span>
              </Typography>
              <Typography variant='body2' className='text-[13px]' style={{ lineHeight: 1.7, marginBottom: '4px' }}>
                <span className='font-bold inline-block'>{t('orderHistoryDetail.totalAmount')}:</span>
                <span className='pl-2 '>
                  {data?.total_amount ? data.total_amount.toLocaleString('vi-VN') + ' VNĐ' : '0 VNĐ'}
                </span>
              </Typography>
              <Typography variant='body2' className='text-[13px]' style={{ lineHeight: 1.7, marginBottom: '4px' }}>
                <span className='font-bold inline-block'>{t('orderHistoryDetail.orderStatus')}:</span>
                <span className='pl-2 font-bold'>{getOrderStatusDisplay(data?.order_status)}</span>
              </Typography>
              <Typography variant='body2' className='text-[13px]' style={{ lineHeight: 1.7, marginBottom: '4px' }}>
                <span className='font-bold inline-block'>{t('orderHistoryDetail.agency')}:</span>
                <span className='pl-2 '>{getAgentName(equipmentData?.agent)}</span>
              </Typography>
            </Box>
            <Box className='mb-5'>
              <Typography className='font-bold text-[16px] my-3' color='#005F73'>
                {t('orderHistoryDetail.eInvoiceTitle')}
              </Typography>
              <Box
                className='p-2 rounded text-[13px] whitespace-pre-wrap'
                style={{ border: '1px solid #d3d0cf', minHeight: '80px', borderRadius: '10px' }}
              >
                <Typography
                  variant='body2'
                  className='text-[13px] flex justify-between'
                  style={{ lineHeight: 1.7, marginBottom: '4px' }}
                >
                  <span className='font-bold inline-block pl-1'> {t('orderHistoryDetail.type')} :</span>
                  <span className='pr-1'>
                    {data?.einvoice_info?.einvoice_type
                      ? t('orderHistoryDetail.company')
                      : t('orderHistoryDetail.personal')}
                  </span>
                </Typography>
                <Typography
                  variant='body2'
                  className='text-[13px] flex justify-between'
                  style={{ lineHeight: 1.7, marginBottom: '4px' }}
                >
                  <span className='font-bold inline-block pl-1'> {t('orderHistoryDetail.name')} :</span>
                  <span className='pr-1'>
                    {data?.einvoice_info?.customer_name || data?.contact_info?.fullname || ''}
                  </span>
                </Typography>
                <Typography
                  variant='body2'
                  className='text-[13px] flex justify-between'
                  style={{ lineHeight: 1.7, marginBottom: '4px' }}
                >
                  <span className='font-bold inline-block pl-1'> {t('orderHistoryDetail.address')}:</span>
                  <span className='pr-1'>
                    {data?.einvoice_info?.customer_address || data?.contact_info?.address || ''}
                  </span>
                </Typography>
                <Typography
                  variant='body2'
                  className='text-[13px] flex justify-between'
                  style={{ lineHeight: 1.7, marginBottom: '4px' }}
                >
                  <span className='font-bold inline-block pl-1'>{t('orderHistoryDetail.email')}:</span>
                  <span className='pr-1'>{data?.einvoice_info?.email || data?.contact_info?.email || ''}</span>
                </Typography>
              </Box>
            </Box>
            <BaseRow className='gap-5 mb-5 !justify-between !items-center'>
              <Box className='w-1/2 hidden'>
                <InputLabel>{t('orderHistoryDetail.filePDF')}:</InputLabel>
                <Box className='w-full'>
                  <Box>
                    {form.values.files?.length
                      ? form.values.files.map((file, index) => (
                          <Box className='flex flex-row gap-1'>
                            <Button
                              className='block font-normal p-0 h-auto'
                              key={index}
                              onClick={() => downloadFile(file)}
                            >
                              • {getFileName(file)}
                            </Button>
                            <IconButton
                              onClick={() => {
                                form.setFieldValue(
                                  'files',
                                  form.values.files?.filter((_, fileIndex) => fileIndex !== index)
                                );
                              }}
                            >
                              <DeleteBin6LineIcon fill='#ED1002' className='w-4' />
                            </IconButton>
                          </Box>
                        ))
                      : ''}
                  </Box>
                </Box>
              </Box>
              <Box className='hidden'>
                <Box {...getRootProps()}>
                  <Box component='input' {...getInputProps()} />
                  <Box className='flex w-[216px] items-center rounded-[8px] p-[7px] border border-[#C6C6C6] cursor-pointer'>
                    <UploadIcon className='w-[20px] h-[20px] mr-auto' />
                    <Box className='mr-auto'>{t('selectFile')}</Box>
                  </Box>
                </Box>
                <Box className='text-[11px] w-[216px] text-center'>{t('dragGuide')}</Box>
              </Box>
            </BaseRow>
            {data?.order_status === 2 && (
              <Button
                variant='contained'
                color='secondary'
                className='min-w-[125px] flex ml-auto hidden'
                onClick={() => form.handleSubmit()}
              >
                {t('save', { ns: 'admin/common' })}
              </Button>
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default memo(OrderHistoryDialog);
